import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import unifiedApi from '../../utils/unifiedApi';
import {
  EMERGENCY_CONTACT_NAME_MAX,
  EMERGENCY_CONTACT_PHONE_ERROR,
  EMERGENCY_CONTACT_PHONE_MAX,
  isValidEmergencyContactPhone,
} from '../../utils/emergencyContact';

const INVALID_LINK_MESSAGE = 'Invalid or expired emergency contact link.';

/** Anonymous page for submitting an emergency contact via a reminder-email token. */
const EmergencyContactPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await unifiedApi.getEmergencyContactTokenInfo(token);
        // unifiedApi maps 404 (e.g. a malformed token) to null.
        if (!data) {
          setError(INVALID_LINK_MESSAGE);
          return;
        }
        setTokenInfo(data);
      } catch (err) {
        setError(err?.message || INVALID_LINK_MESSAGE);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const nameError = touched && !name.trim() ? 'Name is required.' : null;
  const phoneError = touched
    ? (!phone.trim() ? 'Phone number is required.' : (!isValidEmergencyContactPhone(phone) ? EMERGENCY_CONTACT_PHONE_ERROR : null))
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!name.trim() || !isValidEmergencyContactPhone(phone)) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await unifiedApi.submitEmergencyContactViaToken(token, {
        emergencyContactName: name.trim(),
        emergencyContactPhone: phone.trim(),
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Typography variant="body2" color="text.secondary">
          This link may have expired or already been used. Please log in and add your emergency
          contact from your profile page.
        </Typography>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2, textAlign: 'center' }}>
        <Paper sx={{ p: { xs: 3, sm: 4 } }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Emergency contact saved
          </Typography>
          <Typography color="text.secondary">
            Thanks! You can update it any time from your profile page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
      <Paper component="form" onSubmit={handleSubmit} noValidate sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Add Your Emergency Contact
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, wordBreak: 'break-word' }}>
          Hi {tokenInfo?.firstName || 'Player'} {tokenInfo?.lastName || ''},
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Please tell us who to call if something happens on the field. Only you and league staff
          can see this information.
        </Typography>

        {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

        <Stack spacing={2}>
          <TextField
            label="Emergency Contact Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
            slotProps={{ htmlInput: { maxLength: EMERGENCY_CONTACT_NAME_MAX } }}
            required
            fullWidth
          />
          <TextField
            label="Emergency Contact Number"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={!!phoneError}
            helperText={phoneError}
            slotProps={{ htmlInput: { maxLength: EMERGENCY_CONTACT_PHONE_MAX } }}
            required
            fullWidth
          />
          <Button type="submit" variant="contained" size="large" disabled={submitting} fullWidth>
            {submitting ? 'Saving...' : 'Save Emergency Contact'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EmergencyContactPage;
